import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, Check, ExternalLink, Globe2, Info, Loader2 } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { imageReviewApi } from "../../utils/api";

// Module lifetime matches the current page: panel remounts and event navigation
// keep the lock, while a full browser refresh resets it.
const braveSearchUsageByEvent = new Map();

// One "Show more" step, and a mirror of the API's `MAX_IMAGE_SUGGESTION_LIMIT` (JS cannot import
// Python — same manual-sync arrangement the ticket-provider blocklist used before it moved to Postgres). The server
// never reports `has_more` at its ceiling, so this is not what stops the button; it is what stops
// us *asking* past the ceiling, which is a 422 rather than a smaller answer.
const DIRECT_LIMIT_STEP = 6;
const DIRECT_LIMIT_MAX = 24;

function parseCost(value) {
  if (value === null || value === undefined || value === "") return null;
  const cost = Number(value);
  return Number.isFinite(cost) && cost >= 0 ? cost : null;
}

function formatCost(value) {
  const cost = parseCost(value);
  return cost === null ? null : `$${cost.toFixed(3)}`;
}

function warningClasses(severity) {
  return severity === "warning"
    ? "border-yellow-300 bg-yellow-50 text-yellow-800"
    : "border-blue-200 bg-blue-50 text-blue-700";
}

export default function ImageSuggestionsPanel({
  eventId,
  currentImages = [],
  suspiciousUrls = [],
  onChange,
  // false hides the Brave half — the query box, the search button and its cost line — leaving
  // only the images discovered on pages the event already links to. The Event research screen
  // uses that: those pages are free to read and an image published on the page selling the
  // ticket is about this event by construction, which a web image search cannot promise.
  // Direct discovery still auto-loads either way.
  showSearch = true,
  // Discovery reads the pages an event links to, so it is only as current as that list. Pass any
  // value derived from those URLs and a change re-runs it. Callers that cannot change an event's
  // URLs (the image review queue navigates between events and never edits one) leave this null
  // and get exactly the old behaviour: one scan per eventId.
  rescanKey = null,
}) {
  const [query, setQuery] = useState("");
  const [directSuggestions, setDirectSuggestions] = useState([]);
  const [braveSuggestions, setBraveSuggestions] = useState([]);
  const [directDiagnostics, setDirectDiagnostics] = useState(null);
  const [braveDiagnostics, setBraveDiagnostics] = useState(null);
  const [searchAvailable, setSearchAvailable] = useState(true);
  const [searchCostUsd, setSearchCostUsd] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [usedUrl, setUsedUrl] = useState(null);
  const [braveUsage, setBraveUsage] = useState(() => braveSearchUsageByEvent.get(eventId) || null);
  // How many direct suggestions to ask for. Raising it does NOT crawl more pages — the six-source
  // crawl is fixed, and is the expensive half — it widens the API's validation budget and returns
  // more of what that same crawl already found and threw away at the slice. So this starts small
  // for the common case (one good poster, six panels open on the research page) and grows only
  // when an admin asks, which is why the default is not simply the maximum.
  const [directLimit, setDirectLimit] = useState(DIRECT_LIMIT_STEP);
  const requestSequence = useRef(0);
  const braveUsed = Boolean(braveUsage);

  // The limit is an ARGUMENT, not a dependency. Reading it from state would put it in this
  // callback's deps, and the reset effect below keys on the callback — so every "Show more" would
  // re-run a *reset* written for changing event, wiping the Brave results the admin paid for
  // while the module-level single-use lock kept the search button disabled. Nothing could bring
  // them back short of a page refresh.
  const loadDirectSuggestions = useCallback(async (limit) => {
    if (!eventId) return;
    const requestId = ++requestSequence.current;
    setLoading(true);
    setError(null);
    try {
      const data = await imageReviewApi.getDirectSuggestions(eventId, limit);
      if (requestId !== requestSequence.current) return;
      setDirectSuggestions(data.suggestions || []);
      setDirectDiagnostics(data.diagnostics || null);
      setSearchAvailable(Boolean(data.search_available));
      setSearchCostUsd(parseCost(data.search_cost_usd));
      // Fill the box, never overwrite it: the reset below blanks it per event so this seeds the
      // API's default query, while a query the admin typed survives a "Show more".
      setQuery((current) => current || data.query || "");
    } catch (e) {
      if (requestId !== requestSequence.current) return;
      setError(e?.response?.data?.detail || "Could not find image suggestions.");
    } finally {
      if (requestId === requestSequence.current) setLoading(false);
    }
  }, [eventId]);

  // Straight to the loader rather than through state: a fetch is all this needs, and routing it
  // through the effect is what made it a reset.
  const showMoreImages = () => {
    const next = Math.min(directLimit + DIRECT_LIMIT_STEP, DIRECT_LIMIT_MAX);
    setDirectLimit(next);
    loadDirectSuggestions(next);
  };

  const searchBrave = async () => {
    if (!eventId || !query.trim() || braveSearchUsageByEvent.has(eventId)) return;
    const requestId = ++requestSequence.current;
    // Lock before awaiting so double clicks cannot create two paid requests. Keep
    // the lock on uncertain network failures because Brave may still have billed it.
    const pendingUsage = { status: "pending", estimatedCostUsd: null };
    braveSearchUsageByEvent.set(eventId, pendingUsage);
    setBraveUsage(pendingUsage);
    setLoading(true);
    setError(null);
    try {
      const data = await imageReviewApi.searchBrave(eventId, query, 6);
      const estimatedCostUsd = parseCost(data.diagnostics?.estimated_cost_usd);
      const usage = data.diagnostics?.search_error || estimatedCostUsd === null
        ? { status: "unknown", estimatedCostUsd: null }
        : { status: "estimated", estimatedCostUsd };
      braveSearchUsageByEvent.set(eventId, usage);
      if (requestId !== requestSequence.current) return;
      setBraveSuggestions(data.suggestions || []);
      setBraveDiagnostics(data.diagnostics || null);
      setBraveUsage(usage);
    } catch (e) {
      const usage = { status: "unknown", estimatedCostUsd: null };
      braveSearchUsageByEvent.set(eventId, usage);
      if (requestId !== requestSequence.current) return;
      setBraveUsage(usage);
      setError(e?.response?.data?.detail || "Could not search Brave.");
    } finally {
      if (requestId === requestSequence.current) setLoading(false);
    }
  };

  useEffect(() => {
    setQuery("");
    setDirectSuggestions([]);
    setBraveSuggestions([]);
    setDirectDiagnostics(null);
    setBraveDiagnostics(null);
    setBraveUsage(braveSearchUsageByEvent.get(eventId) || null);
    setSearchCostUsd(null);
    setError(null);
    setUsedUrl(null);
    // Back to one step for the new event, and loaded with that value rather than by waiting for
    // the state to land — which is what keeps this effect the only place a reset happens.
    setDirectLimit(DIRECT_LIMIT_STEP);
    loadDirectSuggestions(DIRECT_LIMIT_STEP);

    return () => {
      requestSequence.current += 1;
    };
  }, [eventId, rescanKey, loadDirectSuggestions]);

  const suggestions = [...directSuggestions, ...braveSuggestions]
    .filter((suggestion, index, all) => all.findIndex((item) => item.image_url === suggestion.image_url) === index);

  const handleUseSuggestion = (suggestion) => {
    const imageUrl = suggestion.image_url;
    const suspicious = new Set(suspiciousUrls);
    const currentRefs = currentImages
      .map((value) =>
        typeof value === "string"
          ? { url: value, source: "unknown" }
          : { url: value?.url, source: value?.source || "unknown" }
      )
      .filter((image) => image.url);
    const cleanExisting = currentRefs.filter((image) => image.url !== imageUrl && !suspicious.has(image.url));
    onChange([{ url: imageUrl, source: suggestion.source || "unknown" }, ...cleanExisting]);
    setUsedUrl(imageUrl);
  };

  return (
    <div className="space-y-3">
      {showSearch && (
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          type="search"
          value={query}
          maxLength={200}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Brave search query"
          aria-label="Brave image search query"
          className="h-9 flex-1 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
        {searchAvailable && directDiagnostics && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={searchBrave}
            disabled={loading || braveUsed || !query.trim()}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe2 className="h-4 w-4" />}
            {braveUsed ? "Brave used" : "Search Brave"}
            {!braveUsed && formatCost(searchCostUsd) && (
              <span className="text-[10px] font-normal text-muted-foreground">
                (up to {formatCost(searchCostUsd)})
              </span>
            )}
          </Button>
        )}
      </div>
      )}

      {showSearch && braveUsed && (
        <p className="text-[11px] text-muted-foreground">
          {braveUsage.status === "pending" && "Search in progress"}
          {braveUsage.status === "estimated" && `${formatCost(braveUsage.estimatedCostUsd)} estimated cost`}
          {braveUsage.status === "unknown" && "Search used · Cost unavailable"}
          {" · Refresh the page to search this event again."}
        </p>
      )}

      {loading && !directDiagnostics && (
        <div className="flex items-center gap-2 py-3 text-xs text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Checking attached event pages…
        </div>
      )}

      {error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</p>}

      {!loading && directDiagnostics && (
        <details className="text-[11px] text-muted-foreground">
          <summary className="w-fit cursor-pointer hover:text-foreground">Discovery details</summary>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
            <span>{directDiagnostics.candidates_considered} checked</span>
            <span>{directDiagnostics.page_sources_with_images}/{directDiagnostics.page_sources_attempted} pages with images</span>
            <span>{directDiagnostics.hard_rejected_count} hidden</span>
            {!searchAvailable && <span>Brave unavailable</span>}
            {braveDiagnostics && <span>1 Brave search used</span>}
            {braveDiagnostics?.search_error && <span className="text-yellow-700">Search failed</span>}
          </div>
        </details>
      )}

      {!loading && directDiagnostics && suggestions.length === 0 && !error && (
        <p className="py-3 text-center text-xs text-muted-foreground">
          No direct images found.
        </p>
      )}

      {suggestions.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {suggestions.map((suggestion) => {
            const selected = usedUrl === suggestion.image_url;
            return (
              <div key={suggestion.image_url} className="overflow-hidden rounded-lg border border-border bg-background">
                <a href={suggestion.image_url} target="_blank" rel="noreferrer" title="Open image in a new tab">
                  <img
                    src={suggestion.preview_url || suggestion.image_url}
                    alt={suggestion.title || "Suggested event artwork"}
                    className="aspect-video w-full cursor-zoom-in bg-muted object-cover"
                  />
                </a>
                <div className="space-y-2 p-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-xs font-medium" title={suggestion.title || suggestion.source}>
                        {suggestion.title || suggestion.source}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {suggestion.source}
                        {suggestion.width && suggestion.height
                          ? ` · ${suggestion.width} x ${suggestion.height}`
                          : ""}
                      </p>
                    </div>
                    <span
                      className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                        suggestion.confidence === "review"
                          ? "bg-blue-50 text-blue-700"
                          : "bg-green-50 text-green-700"
                      }`}
                    >
                      {suggestion.confidence === "provider_rule"
                        ? `${suggestion.rule} event image`
                        : suggestion.confidence === "high"
                          ? "Linked page"
                          : "Web result"}
                    </span>
                  </div>

                  {suggestion.warnings?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {suggestion.warnings.map((warning) => (
                        <span
                          key={warning.code}
                          title={warning.message}
                          className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] ${warningClasses(warning.severity)}`}
                        >
                          {warning.severity === "warning" ? (
                            <AlertTriangle className="h-2.5 w-2.5" />
                          ) : (
                            <Info className="h-2.5 w-2.5" />
                          )}
                          {warning.message}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      className="h-7 flex-1 gap-1 text-xs"
                      onClick={() => handleUseSuggestion(suggestion)}
                    >
                      {selected && <Check className="h-3.5 w-3.5" />}
                      {selected ? "Selected" : "Use image"}
                    </Button>
                    {suggestion.source_page_url && (
                      <a
                        href={suggestion.source_page_url}
                        target="_blank"
                        rel="noreferrer"
                        title="Open source page"
                        className="inline-flex h-7 items-center gap-1 rounded-md border border-input px-2 text-xs hover:bg-muted"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        Source
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Only when the API says a bigger limit would actually return something — otherwise this
          is a button that reloads the same six images. It costs no extra page crawl and no Brave
          request; it re-validates more of what the same crawl already found. */}
      {!loading && directDiagnostics?.has_more && directLimit < DIRECT_LIMIT_MAX && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 w-full gap-1 text-xs"
          onClick={showMoreImages}
        >
          Show more images
        </Button>
      )}
    </div>
  );
}
